import { useState, useEffect, useCallback, useRef } from 'react';
import { useACL } from './ACLContext';
import { ACLPermission, PermissionCheckRequest, bulkPermissionResponseKey } from '../api/aclApi';
import {
    clearPermissionCacheStore,
    getCachedPermissionEntry,
    getPermissionCache,
    getPermissionCacheEpoch,
    permissionCacheKeyForRequest,
    PERMISSION_CACHE_DURATION_MS,
    subscribePermissionCacheInvalidation,
    warmPermissionCacheFromBulkResults,
} from './permissionCacheStore';

/** Bumps when {@link clearPermissionCache} runs so hooks refetch without re-login. */
function usePermissionCacheEpoch(): number {
    const [epoch, setEpoch] = useState(() => getPermissionCacheEpoch());

    useEffect(() => {
        return subscribePermissionCacheInvalidation(() => {
            setEpoch(getPermissionCacheEpoch());
        });
    }, []);

    return epoch;
}

interface UsePermissionOptions {
    /**
     * If true, automatically refetch permission when dependencies change
     * Default: true
     */
    enabled?: boolean;
    /**
     * Cache duration in milliseconds
     * Default: 60000 (1 minute)
     */
    cacheDuration?: number;
}

interface UsePermissionResult {
    /** Whether the user has permission */
    allowed: boolean;
    /** Whether the permission check is loading */
    loading: boolean;
    /** Error if permission check failed */
    error: Error | null;
    /** Reason for the permission result */
    reason: string;
    /** Permissions that matched the check */
    matchedPermissions: ACLPermission[];
    /** Manually refetch the permission */
    refetch: () => Promise<void>;
}

/** Stable key for bulk request lists — avoids refetch loops when callers pass a new array each render. */
function permissionRequestsContentKey(requests: PermissionCheckRequest[]): string {
    if (requests.length === 0) return '';
    return requests
        .map((r) => {
            const owner = r.itemOwnerId || r.itemCreatorId || '';
            return `${r.action}:${r.targetType}:${r.targetId}:${r.collectionUid ?? ''}:${owner}:${r.fieldKey ?? ''}`;
        })
        .sort()
        .join('\x1e');
}

/**
 * Hook to check if user has permission to perform an action
 *
 * @example
 * ```tsx
 * const { allowed, loading } = usePermission({
 *   action: 'update',
 *   targetType: 'item',
 *   targetId: 'event_123'
 * });
 *
 * if (loading) return <Spinner />;
 * if (allowed) return <EditButton />;
 * return null;
 * ```
 */
export function usePermission(
    request: PermissionCheckRequest | null,
    options: UsePermissionOptions = {}
): UsePermissionResult {
    const { enabled = true, cacheDuration = 60000 } = options;
    const { checkPermission } = useACL();
    const permissionCacheEpoch = usePermissionCacheEpoch();

    const [allowed, setAllowed] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    const [reason, setReason] = useState<string>('');
    const [matchedPermissions, setMatchedPermissions] = useState<ACLPermission[]>([]);

    // Use ref to track if component is mounted
    const isMounted = useRef(true);

    const fetchPermission = useCallback(async () => {
        if (!request || !enabled) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Check cache first
            const cacheKey = permissionCacheKeyForRequest(request);
            const cached = getPermissionCache().get(cacheKey);
            const now = Date.now();

            if (cached && (now - cached.timestamp) < cacheDuration) {
                // Use cached result
                if (isMounted.current) {
                    setAllowed(cached.allowed);
                    setReason(cached.reason);
                    setMatchedPermissions(cached.matchedPermissions);
                    setLoading(false);
                }
                return;
            }

            // Fetch from API
            const result = await checkPermission(request);

            // Update cache
            getPermissionCache().set(cacheKey, {
                allowed: result.allowed,
                reason: result.reason,
                matchedPermissions: result.matchedPermissions || [],
                timestamp: now
            });

            if (isMounted.current) {
                setAllowed(result.allowed);
                setReason(result.reason);
                setMatchedPermissions(result.matchedPermissions || []);
            }
        } catch (err: any) {
            console.error('usePermission: Error checking permission:', err);
            if (isMounted.current) {
                setError(err);
                setAllowed(false);
                setReason(err.message || 'Permission check failed');
                setMatchedPermissions([]);
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    }, [request, enabled, checkPermission, cacheDuration, permissionCacheEpoch]);

    useEffect(() => {
        isMounted.current = true;
        fetchPermission();

        return () => {
            isMounted.current = false;
        };
    }, [fetchPermission]);

    return {
        allowed,
        loading,
        error,
        reason,
        matchedPermissions,
        refetch: fetchPermission
    };
}

/**
 * Hook to check multiple permissions at once
 *
 * @example
 * ```tsx
 * const permissions = usePermissions([
 *   { action: 'read', targetType: 'collection', targetId: 'events' },
 *   { action: 'create', targetType: 'collection', targetId: 'events' },
 *   { action: 'update', targetType: 'item', targetId: 'event_123' }
 * ]);
 *
 * if (permissions.loading) return <Spinner />;
 *
 * return (
 *   <>
 *     {permissions.results[0].allowed && <ViewButton />}
 *     {permissions.results[1].allowed && <CreateButton />}
 *     {permissions.results[2].allowed && <EditButton />}
 *   </>
 * );
 * ```
 */
export function usePermissions(
    requests: PermissionCheckRequest[],
    options: UsePermissionOptions = {}
): {
    results: UsePermissionResult[];
    loading: boolean;
    refetch: () => Promise<void>;
} {
    const { enabled = true, cacheDuration = PERMISSION_CACHE_DURATION_MS } = options;
    const { checkBulkPermissions } = useACL();
    const permissionCacheEpoch = usePermissionCacheEpoch();

    const requestsRef = useRef(requests);
    requestsRef.current = requests;

    const requestsContentKey = permissionRequestsContentKey(requests);

    const [results, setResults] = useState<UsePermissionResult[]>(
        requests.map(() => ({
            allowed: false,
            loading: true,
            error: null,
            reason: '',
            matchedPermissions: [],
            refetch: async () => { }
        }))
    );
    const [loading, setLoading] = useState<boolean>(true);

    const isMounted = useRef(true);

    const fetchPermissions = useCallback(async () => {
        const reqs = requestsRef.current;
        if (!enabled || reqs.length === 0) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);

            const cachedAll = reqs.map((req) => getCachedPermissionEntry(req, cacheDuration));
            if (cachedAll.every((c) => c != null)) {
                const newResults = reqs.map((req, i) => {
                    const c = cachedAll[i]!;
                    return {
                        allowed: c.allowed,
                        loading: false,
                        error: null,
                        reason: c.reason,
                        matchedPermissions: c.matchedPermissions,
                        refetch: fetchPermissions
                    };
                });
                if (isMounted.current) {
                    setResults(newResults);
                }
                return;
            }

            const bulkResults = await checkBulkPermissions(reqs);
            warmPermissionCacheFromBulkResults(reqs, bulkResults);

            const newResults = reqs.map((req) => {
                const key = bulkPermissionResponseKey(req);
                const allowed = bulkResults[key] ?? false;

                return {
                    allowed,
                    loading: false,
                    error: null,
                    reason: allowed ? 'Allowed' : 'Denied',
                    matchedPermissions: [],
                    refetch: fetchPermissions
                };
            });

            if (isMounted.current) {
                setResults(newResults);
            }
        } catch (err: any) {
            console.error('usePermissions: Error checking permissions:', err);
            if (isMounted.current) {
                setResults(
                    requestsRef.current.map(() => ({
                        allowed: false,
                        loading: false,
                        error: err,
                        reason: err.message || 'Permission check failed',
                        matchedPermissions: [],
                        refetch: fetchPermissions
                    }))
                );
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    }, [requestsContentKey, enabled, checkBulkPermissions, cacheDuration, permissionCacheEpoch]);

    useEffect(() => {
        isMounted.current = true;
        fetchPermissions();

        return () => {
            isMounted.current = false;
        };
    }, [fetchPermissions]);

    return {
        results,
        loading,
        refetch: fetchPermissions
    };
}

/**
 * Clear permission cache
 * Useful when user roles/permissions have changed
 */
export function clearPermissionCache(): void {
    clearPermissionCacheStore();
}
